import { useState, useRef, useEffect } from "react";
import { Plus, Activity, X, Eye, EyeOff, Moon } from "lucide-react";
import clsx from "clsx";
import { ColorPicker } from "@/components/ui/ColorPicker";
import {
  useIndicatorsStore,
  labelFor,
  isMarkerIndicator,
  type IndicatorType,
  type LineStyle,
  type LineWidth,
} from "@/store/indicators";

const ADDABLE: { type: IndicatorType; label: string; desc: string }[] = [
  { type: "ema",  label: "EMA",             desc: "Exponential Moving Average" },
  { type: "sma",  label: "SMA",             desc: "Simple Moving Average" },
  { type: "vwap", label: "VWAP",            desc: "Volume Weighted Average Price (daily-anchored)" },
  { type: "bb",   label: "Bollinger Bands", desc: "Volatility envelope around an SMA" },
  { type: "mfi",  label: "Money Flow Index",desc: "Volume-weighted RSI · separate pane below chart" },
  { type: "moon", label: "Moon Phases",     desc: "New/Full/Quarter markers on candles" },
];

const LINE_WIDTHS: LineWidth[] = [1, 2, 3];
const LINE_STYLES: { value: LineStyle; label: string; preview: string }[] = [
  { value: "solid",  label: "Solid",  preview: "━━━━" },
  { value: "dashed", label: "Dashed", preview: "╌╌╌╌" },
  { value: "dotted", label: "Dotted", preview: "┄┄┄┄" },
];

export function IndicatorsButton() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    list,
    add,
    remove,
    toggle,
    updateParams,
    updateColor,
    updateLineWidth,
    updateLineStyle,
  } = useIndicatorsStore();

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-md border border-border bg-bg-elevated px-2.5 py-1 text-xs font-medium text-text-secondary transition-colors hover:border-border-strong hover:text-text-primary"
      >
        <Activity className="h-3.5 w-3.5" />
        <span>Indicators</span>
        {list.length > 0 && (
          <span className="rounded bg-accent/20 px-1.5 py-px text-2xs text-accent">
            {list.filter((i) => i.enabled).length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-full z-30 mt-1.5 w-[24rem] rounded-md border border-border bg-bg-elevated shadow-elevated animate-slide-up">
          {list.length > 0 && (
            <div className="border-b border-border max-h-[24rem] overflow-y-auto">
              <div className="sticky top-0 bg-bg-elevated px-3 py-1.5 text-2xs font-semibold uppercase tracking-wider text-text-muted">
                Active
              </div>
              {list.map((ind) => {
                const isMoon = ind.type === "moon";
                return (
                  <div
                    key={ind.id}
                    className="flex flex-col gap-1.5 border-b border-border/50 px-3 py-2 last:border-b-0 hover:bg-bg-hover/40"
                  >
                    <div className="flex items-center gap-2">
                      {isMoon ? (
                        <Moon className="h-3.5 w-3.5 text-text-muted" />
                      ) : (
                        <ColorPicker
                          value={ind.color}
                          onChange={(c) => updateColor(ind.id, c)}
                          size={14}
                          ariaLabel={`Color for ${labelFor(ind)}`}
                        />
                      )}
                      <div className="flex-1 truncate text-xs text-text-primary">
                        {labelFor(ind)}
                      </div>

                      {/* Period field — EMA / SMA / BB / MFI all have a period */}
                      {(ind.type === "ema" || ind.type === "sma" || ind.type === "bb" || ind.type === "mfi") && (
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-text-muted">P</span>
                          <input
                            type="number"
                            value={ind.params.period}
                            min={2}
                            max={500}
                            onChange={(e) =>
                              updateParams(ind.id, {
                                period: Math.max(2, Math.min(500, Number(e.target.value) || 0)),
                              })
                            }
                            className="w-12 rounded bg-bg-deep px-1 py-0.5 text-2xs font-mono tabular text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
                            title="Period"
                          />
                        </div>
                      )}

                      {/* BB extra: standard deviation */}
                      {ind.type === "bb" && (
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-text-muted">σ</span>
                          <input
                            type="number"
                            value={ind.params.deviation}
                            step="0.1"
                            min={0.5}
                            max={5}
                            onChange={(e) =>
                              updateParams(ind.id, {
                                deviation: Math.max(0.5, Math.min(5, Number(e.target.value) || 0)),
                              })
                            }
                            className="w-12 rounded bg-bg-deep px-1 py-0.5 text-2xs font-mono tabular text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
                            title="Standard deviation multiplier"
                          />
                        </div>
                      )}

                      <button
                        onClick={() => toggle(ind.id)}
                        className="text-text-muted hover:text-text-primary"
                        title={ind.enabled ? "Hide" : "Show"}
                      >
                        {ind.enabled ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        onClick={() => remove(ind.id)}
                        className="text-text-muted hover:text-bear"
                        title="Remove"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    {/* Width + Style — not applicable to marker indicators (moon) */}
                    {!isMarkerIndicator(ind.type) && (
                      <div className="flex items-center gap-2 text-2xs">
                        <span className="text-text-muted">Width</span>
                        <div className="flex items-center gap-px rounded border border-border bg-bg-deep p-0.5">
                          {LINE_WIDTHS.map((w) => (
                            <button
                              key={w}
                              onClick={() => updateLineWidth(ind.id, w)}
                              className={clsx(
                                "rounded px-1.5 py-0.5 font-mono transition-colors",
                                ind.lineWidth === w
                                  ? "bg-accent text-white"
                                  : "text-text-secondary hover:bg-bg-hover hover:text-text-primary"
                              )}
                            >
                              {w}
                            </button>
                          ))}
                        </div>

                        <span className="ml-2 text-text-muted">Style</span>
                        <div className="flex items-center gap-px rounded border border-border bg-bg-deep p-0.5">
                          {LINE_STYLES.map((s) => (
                            <button
                              key={s.value}
                              onClick={() => updateLineStyle(ind.id, s.value)}
                              className={clsx(
                                "rounded px-1.5 py-0.5 font-mono transition-colors",
                                ind.lineStyle === s.value
                                  ? "bg-accent text-white"
                                  : "text-text-secondary hover:bg-bg-hover hover:text-text-primary"
                              )}
                              title={s.label}
                            >
                              {s.preview}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div>
            <div className="px-3 py-1.5 text-2xs font-semibold uppercase tracking-wider text-text-muted">
              Add indicator
            </div>
            {ADDABLE.map(({ type, label, desc }) => (
              <button
                key={type}
                onClick={() => { add(type); setOpen(false); }}
                className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left transition-colors hover:bg-bg-hover"
              >
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-text-primary">{label}</span>
                  <span className="text-2xs text-text-muted">{desc}</span>
                </div>
                <Plus className="h-3.5 w-3.5 text-text-muted" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
